import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label: string;
  required?: boolean;
  error?: string;
  helper?: string;
  /** Slot for an in-input button (e.g. password eye toggle). */
  controlEnd?: ReactNode;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, required, error, helper, controlEnd, className, ...rest },
  ref,
) {
  const generatedId = useId();
  const id = rest.name ? `${rest.name}-${generatedId}` : generatedId;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, !error && helper ? helperId : null].filter(Boolean).join(' ') ||
    undefined;

  const inputClasses = [
    'm5-field__input',
    controlEnd ? 'm5-field__input--has-toggle' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`m5-field${error ? ' m5-field--error' : ''}`}>
      <label className="m5-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="m5-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="m5-field__control">
        <input
          ref={ref}
          id={id}
          className={inputClasses}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          {...rest}
        />
        {controlEnd}
      </div>
      {error ? (
        <span id={errorId} className="m5-field__error" role="alert">
          {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="m5-field__helper">
          {helper}
        </span>
      ) : null}
    </div>
  );
});
